import { useTranslation } from "react-i18next";
import type { Decision } from "../../../api/access/decisions";
import { changeProjectRules, type ProjectSettings } from "../../../api/settingsApi";
import ReasonedChangeForm from "../../../ui/forms/ReasonedChangeForm";
import Section from "../../../ui/forms/Section";
import { useProjects } from "../../projectContext";
import ProjectRulesFields from "./ProjectRulesFields";
import RuleHistory from "./RuleHistory";

interface RulesSectionsProps {
  projectId: string;
  settings: ProjectSettings;
  /** The project's decision on changing its rules. */
  changing: Decision;
  onChanged: () => void;
}

/**
 * The project's rules (accounts.md ST-1), as far as they act (redesign R3-2 "Project rules"):
 * each with who changed it last, when and why; changing them needs a reason. Publishing to
 * ResearchSpace (knowledge-data.md K-13) is one of these rules; module D applies it. A library
 * may list the projects that pin it from those the user can see (JD-2). What the installation
 * sets is the administrators' page, not repeated here.
 */
export default function RulesSections({ projectId, settings, changing, onChanged }: RulesSectionsProps) {
  const { t } = useTranslation();
  const { projects } = useProjects();
  const pinningChoices = projects.status === "loaded" ? projects.value.filter((project) => project.projectId !== projectId) : [];
  return (
    <div className="max-w-4xl space-y-8">
      <Section title={t("projects.settings.rules.title")}>
        <ReasonedChangeForm
          value={settings.rules}
          requires={changing}
          onSave={async (rules, reason) => { await changeProjectRules(projectId, rules, reason); onChanged(); }}
        >
          {(rules, onChange) => <ProjectRulesFields rules={rules} pinningChoices={pinningChoices} onChange={onChange} />}
        </ReasonedChangeForm>
        <RuleHistory lastChanges={settings.lastChanges} />
      </Section>
    </div>
  );
}
